import { useMutation } from '@apollo/client'
import { Modal, message } from 'antd'
import Router, { useRouter } from 'next/router'
import { useMemo, useState } from 'react'
import { PAGE_SIZE } from '~/configs'
import { AI_ARTWORK_POST_SAME, FAV_POST, LIKE_POST } from '~/graphqls/gqls'
import {
  ActionType,
  AffectedType,
  AiArtworkPostSameInput,
  AiArtworkSameType,
  AiArtworkServiceTypeGraphQLEnum,
  AiCreateArtworkModel,
  FollowStatus,
  PostTypes,
  SocialViewType,
  SquarePostScope,
  SquarePostType,
  VisibleType,
  aiArtworkPostSame,
  aiArtworkPostSameVariables,
  favPost,
  favPostVariables,
  likePost,
  likePostVariables,
} from '~/graphqls/types'
import { graphQLErrorCode, graphQLErrorMessage, setScrollCache } from '~/utils'
import { useDebounce } from './useDebounce'
import { useMjCreateValidation } from './useMjCreateValidation'
import { useStores } from './useStores'
import { useValidateAuth } from './useValidateAuth'
import { useValidateIntergal } from './useValidateIntergal'
import { useValidateVip } from './useValidateVip'

type TOptions = {
  sameAgainSuccess?: () => void
  onActionComplete?: (params?: { type?: string; id?: any }) => void
}
/** 一键同款参数类型 */
export type sameAgainProps = AiArtworkPostSameInput & {
  /** 同款中文名称: 错误文案拼接*/
  sameTypeName?: string
  selectModel?: AiCreateArtworkModel
}
export const usePostSocial = (post: PostTypes, options?: TOptions) => {
  const [likePostAction] = useMutation<likePost, likePostVariables>(LIKE_POST)
  const [favPostAction] = useMutation<favPost, favPostVariables>(FAV_POST)
  const [createPostAction] = useMutation<
    aiArtworkPostSame,
    aiArtworkPostSameVariables
  >(AI_ARTWORK_POST_SAME)

  const { validateIsLogin } = useValidateAuth()
  const { vipModal } = useValidateVip()
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const { validMjCreate } = useMjCreateValidation()
  const router = useRouter()

  const [liked, setLiked] = useState(
    post?.itemSocialInfo?.status?.liked || false
  )
  const [faved, setFaved] = useState(
    post?.itemSocialInfo?.status?.collected || false
  )
  const [likeCount, setLikeCount] = useState(
    post?.itemSocialInfo?.likesCount || 0
  )
  const [favCount, setFavCount] = useState(
    post?.itemSocialInfo?.collectsCount || 0
  )

  const { store } = useStores()

  const canSameAgain = useMemo(() => {
    if (store.userinfo?.base?.uid === post?.user?.uid) return true
    const followStatus = post?.user?.social?.followStatus
    if (post?.visibleType === VisibleType.ONLY_FANS) {
      return (
        followStatus === FollowStatus.ME_ONE_WAY ||
        followStatus === FollowStatus.TWO_WAY
      )
    } else if (post?.visibleType === VisibleType.ONLY_FOLLOW_EACH_OTHER) {
      return followStatus === FollowStatus.TWO_WAY
    } else if (post?.visibleType === VisibleType.ONLY_VOTED) {
      // 投票可见
      return !post?.aiArtworkInfo?.hiddenBaseInfo
    }
    return true
  }, [post, store.userinfo])

  const showSameAgain = useMemo(() => {
    if (store.userinfo?.base?.uid === post?.user?.uid) return true
    const followStatus = post?.user?.social?.followStatus
    if (post?.visibleType === VisibleType.ONLY_FANS) {
      return true
    } else if (post?.visibleType === VisibleType.ONLY_FOLLOW_EACH_OTHER) {
      return followStatus === FollowStatus.TWO_WAY
    } else if (post?.visibleType === VisibleType.ONLY_VOTED) {
      // 投票可见
      return true
    }
    return true
  }, [post, store.userinfo])

  const handleLike = () => {
    if (!validateIsLogin()) return
    let affectedType =
      post.postScope === SquarePostScope.INSIDE
        ? AffectedType.AI_ARTWORK
        : AffectedType.EXTERNAL_AI_ARTWORK
    if (post.type === SquarePostType.NOTE) {
      affectedType = AffectedType.NOTE
    } else if (post.type === SquarePostType.AI_COLLECTION) {
      affectedType = AffectedType.AI_COLLECTION
    } else if (post.type === SquarePostType.AI_COMIC) {
      affectedType = AffectedType.AI_COMIC
    }
    try {
      likePostAction({
        variables: {
          input: {
            actionType: !liked ? ActionType.CONFIRM : ActionType.CANCEL,
            affectedType,
            id: post.id,
            viewType: SocialViewType.SQUARE,
          },
        },
      })
      setLikeCount(!liked ? likeCount + 1 : likeCount - 1)
      setLiked(!liked)
      options?.onActionComplete?.({
        type: 'liked',
        id: post.id,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleFav = () => {
    if (!validateIsLogin()) return
    let affectedType =
      post.postScope === SquarePostScope.INSIDE
        ? AffectedType.AI_ARTWORK
        : AffectedType.EXTERNAL_AI_ARTWORK
    if (post.type === SquarePostType.NOTE) {
      affectedType = AffectedType.NOTE
    } else if (post.type === SquarePostType.AI_COLLECTION) {
      affectedType = AffectedType.AI_COLLECTION
    } else if (post.type === SquarePostType.AI_COMIC) {
      affectedType = AffectedType.AI_COMIC
    }
    try {
      favPostAction({
        variables: {
          input: {
            actionType: !faved ? ActionType.CONFIRM : ActionType.CANCEL,
            affectedType,
            id: post.id,
            viewType: SocialViewType.SQUARE,
          },
        },
      })
      setFavCount(!faved ? favCount + 1 : favCount - 1)
      setFaved(!faved)
      options?.onActionComplete?.({
        type: 'fav',
        id: post.id,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const handleSearch = () => {
    Router.push({
      pathname: '/',
      query: {
        image: post.coverImage?.imageUrl as string,
      },
    })
  }

  const requestSameAgain = async (param: sameAgainProps) => {
    const { sameType, pictureSameModel, imageId, sameTypeName } = param
    try {
      const res = await createPostAction({
        variables: {
          param: {
            postId: post.id,
            sameType,
            pictureSameModel,
            imageId,
          },
        },
      })
      options?.sameAgainSuccess?.()
      if (
        post.imageList?.[0].aiArtwork?.serviceType ===
        AiArtworkServiceTypeGraphQLEnum.PROFESSIONAL
      ) {
        window.open(`${process.env.NEXT_PUBLIC_WUJIEAI_PRO_HOST}/ai/task`)
      } else if (
        post?.aiArtworkInfo?.model === AiCreateArtworkModel.MIDJOURNEY
      ) {
        setScrollCache({
          key: '/ai/mine',
          value: { path: '/ai/mine', scrollTop: 0, pageSize: PAGE_SIZE },
        })
        router.push('/ai/mine')
      } else {
        router.push(
          `/ai/${res?.data?.squareModule?.aiArtworkId}?visibleType=${post?.visibleType}`
        )
      }
    } catch (err) {
      if (graphQLErrorCode(err) === 20111001) {
        vipModal('此作品由VIP模型生成，仅限VIP会员专享使用')
        return
      }

      if (IsNotEnough(err)) {
        intergalModal()
      } else {
        message.destroy()
        message.error(
          graphQLErrorMessage(err) || sameTypeName + '操作失败，请稍后再试~'
        )
      }
    }
  }

  /** 一键同款（包括图生图） */
  const handleSameAgain = useDebounce(
    (
      param: sameAgainProps = {
        // 默认一键同款
        sameType: AiArtworkSameType.CREATE_LIKEWISE,
        sameTypeName: '一键同款',
      }
    ) => {
      if (!validateIsLogin()) return
      validMjCreate({
        model: post?.aiArtworkInfo?.model,
        selectModel: param.selectModel,
        isSameType: true,
        onConfirm: () => {
          requestSameAgain(param)
        },
      })
    },
    [post]
  )

  return {
    /**点赞状态 */
    liked,
    /**点赞数量 */
    likeCount,
    /**点赞操作 */
    handleLike,
    /**收藏状态 */
    faved,
    /**收藏数量 */
    favCount,
    /**收藏操作 */
    handleFav,
    /**搜相似 */
    handleSearch,
    /**一键同款 */
    handleSameAgain,
    /**展示一键同款按钮 */
    showSameAgain,
    /** 是否可以一键同款按钮 */
    canSameAgain,
  }
}
